import { BusinessPlanSection, SectionId, SectionIdEnum, BusinessPlanSubsection, SECTION_METADATA, SECTION_MARKERS, SectionStatus } from '../../types/businessPlan';
import { APIResponse } from './types';

export class ResponseHandler {
  static parseResponse(type: string, response: APIResponse): any {
    switch (type) {
      case 'section':
        return ResponseHandler.formatSection(response, 'overview' as SectionId);
      case 'completePlan':
        return ResponseHandler.parseCompletePlan(response);
      default:
        throw new Error(`Unknown response type: ${type}`);
    }
  }

  static parseCompletePlan(response: any): Record<SectionId, BusinessPlanSection> {
    try {
      console.log('Parsing complete plan response:', response);
      
      // Initialize with empty sections
      const sections = ResponseHandler.createEmptySections();
      
      // If the response is already in the correct format (sections object)
      if (response && typeof response === 'object') {
        // If the response is a direct mapping of section IDs to content
        const hasSectionIds = Object.keys(response).some(key => 
          Object.values(SectionIdEnum).includes(key as any)
        );
        
        if (hasSectionIds) {
          console.log('Response has section IDs');
          Object.entries(response).forEach(([key, value]: [string, any]) => {
            // Only process valid section IDs
            if (Object.values(SectionIdEnum).includes(key as any)) {
              const sectionId = key as SectionId;
              const metadata = SECTION_METADATA[sectionId];
              
              // Extract content based on the type of value
              let content = '';
              if (typeof value === 'string') {
                content = value;
              } else if (value && typeof value === 'object' && 'content' in value) {
                content = value.content || '';
              }
              
              // Only update if we have content
              if (content && content.trim()) {
                sections[sectionId] = {
                  id: sectionId,
                  title: metadata.title,
                  content,
                  status: 'completed',
                  pageNumber: metadata.pageNumber
                };
              }
            }
          });
          return sections;
        }
        // Check if the response has a sections property
        else if (response.sections && typeof response.sections === 'object') {
          console.log('Response has a sections property');
          return ResponseHandler.parseCompletePlan(response.sections);
        }
        // Check if the response has a content property that is a string
        else if (response.content && typeof response.content === 'string') {
          console.log('Response has a content property');
          return ResponseHandler.parseContentToSections(response.content);
        }
      }
      // If the response is a string, try to parse it as content
      else if (typeof response === 'string') {
        console.log('Response is a string, parsing as content');
        return ResponseHandler.parseContentToSections(response);
      }
      
      return sections;
    } catch (error) {
      console.error('Error parsing complete plan:', error);
      return ResponseHandler.createEmptySections();
    }
  }
  
  // Parse raw content string into sections
  static parseContentToSections(content: string): Record<SectionId, BusinessPlanSection> {
    // Start with empty sections
    const sections = ResponseHandler.createEmptySections();
    
    if (!content || typeof content !== 'string') {
      console.warn('Invalid content provided to parseContentToSections');
      return sections;
    }
    
    try {
      // Split content by lines
      const lines = content.split('\n');
      let currentSection: SectionId | null = null;
      let currentContent: string[] = [];
      
      // Process each line
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        // Skip empty lines
        if (!line) continue;
        
        // Check if this line is a section header
        const sectionId = ResponseHandler.identifySection(line);
        
        if (sectionId) {
          // If we were already processing a section, save its content
          if (currentSection && currentContent.length > 0) {
            sections[currentSection].content = currentContent.join('\n');
            sections[currentSection].status = 'completed';
          }
          
          // Start a new section
          currentSection = sectionId;
          currentContent = [];
        } else if (currentSection) {
          // Add this line to the current section's content
          currentContent.push(lines[i]);
        }
      }
      
      // Save the last section's content
      if (currentSection && currentContent.length > 0) {
        sections[currentSection].content = currentContent.join('\n');
        sections[currentSection].status = 'completed';
      }
      
      return sections;
    } catch (error) {
      console.error('Error parsing content to sections:', error);
      return sections;
    }
  }

  // Format a section response
  static formatSection(response: any, sectionId: SectionId): BusinessPlanSection {
    try {
      console.log(`Formatting section ${sectionId}, raw response:`, response);
      
      // Get the metadata for this section
      const metadata = SECTION_METADATA[sectionId];
      
      // Direct check for the exact response format from our API
      if (response && typeof response === 'object' && 
          'id' in response && response.id === sectionId &&
          'content' in response && typeof response.content === 'string') {
        
        console.log(`Found direct match with API response format. Content length: ${response.content.length}`);
        
        // Return the section directly - it's already in the right format
        return {
          id: sectionId,
          title: metadata.title,
          content: response.content,
          status: 'completed',
          pageNumber: metadata.pageNumber,
          subsections: []
        };
      }
      
      // If we didn't find the direct format, extract content from other formats
      let content = '';
      let status: SectionStatus = 'not-started';
      
      // Case 1: Response is a string directly
      if (typeof response === 'string') {
        console.log(`Response is a string, length: ${response.length}`);
        content = response;
        status = 'completed';
      } 
      // Case 2: Response is an object
      else if (response && typeof response === 'object') {
        console.log(`Response is an object with keys: ${Object.keys(response).join(', ')}`);
        
        // Check for content in various places
        if ('content' in response && typeof response.content === 'string') {
          content = response.content;
          status = 'completed';
        } 
        else if (sectionId in response && typeof response[sectionId] === 'string') {
          content = response[sectionId];
          status = 'completed';
        }
        // If there's a text property (common in AI responses)
        else if ('text' in response && typeof response.text === 'string') {
          content = response.text;
          status = 'completed';
        }
        // If there's a message property with content (Anthropic format)
        else if ('message' in response && 
                 typeof response.message === 'object' && 
                 'content' in response.message) {
          if (Array.isArray(response.message.content)) {
            // Join any array of content blocks
            content = response.message.content
              .filter((block: any) => block.type === 'text')
              .map((block: any) => block.text)
              .join('\n\n');
          } else if (typeof response.message.content === 'string') {
            content = response.message.content;
          }
          status = 'completed';
        }
        // If response has a choices array (OpenAI format)
        else if ('choices' in response && Array.isArray(response.choices) && response.choices.length > 0) {
          const choice = response.choices[0];
          if (choice.message && choice.message.content) {
            content = choice.message.content;
            status = 'completed';
          }
        }
      }
      
      // Ensure we have content
      if (!content || content.trim() === '') {
        console.warn(`No content found in response for section ${sectionId}`);
        content = `Content for ${metadata.title} section is currently unavailable.`;
        status = 'error';
      } else {
        console.log(`Found content for section ${sectionId}, length: ${content.length}`);
      }
      
      return {
        id: sectionId,
        title: metadata.title,
        content: content,
        status: status,
        pageNumber: metadata.pageNumber,
        subsections: [] // Will be filled in later if needed
      };
    } catch (error) {
      console.error(`Error formatting section ${sectionId}:`, error);
      return ResponseHandler.createEmptySection(sectionId);
    }
  }

  // Extract subsections from content
  static extractSubsections(content: string, sectionId: SectionId): BusinessPlanSection['subsections'] {
    if (!content) return [];
    
    try {
      // Split content by lines
      const lines = content.split('\n');
      const subsections: BusinessPlanSection['subsections'] = [];
      let currentTitle = '';
      let currentContent: string[] = [];
      let subsectionIndex = 0;
      
      // Process each line to identify subsection headers and content
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        // Skip empty lines
        if (!line) continue;
        
        // Check if this line looks like a subsection header
        // (e.g., starts with a number, has a title-like format)
        const isSubsectionHeader = /^\d+\.\s|^[A-Z][^.!?]*:/.test(line);
        
        if (isSubsectionHeader) {
          // If we were already collecting content for a subsection, save it
          if (currentTitle && currentContent.length > 0) {
            const id = `${sectionId}-subsection-${subsectionIndex}`;
            subsections.push({
              id,
              title: currentTitle,
              content: currentContent.join('\n'),
              status: 'completed',
              pageNumber: SECTION_METADATA[sectionId].pageNumber + subsectionIndex
            });
            subsectionIndex++;
          }
          
          // Start a new subsection
          currentTitle = line;
          currentContent = [];
        } else if (currentTitle) {
          // Add this line to the current subsection's content
          currentContent.push(line);
        }
      }
      
      // Save the last subsection if there is one
      if (currentTitle && currentContent.length > 0) {
        const id = `${sectionId}-subsection-${subsectionIndex}`;
        subsections.push({
          id,
          title: currentTitle,
          content: currentContent.join('\n'),
          status: 'completed',
          pageNumber: SECTION_METADATA[sectionId].pageNumber + subsectionIndex
        });
      }
      
      return subsections;
    } catch (error) {
      console.error('Error extracting subsections:', error);
      return [];
    }
  }

  // Create an empty section with the given ID
  static createEmptySection(sectionId: SectionId): BusinessPlanSection {
    const metadata = SECTION_METADATA[sectionId] || { title: 'Unknown Section', pageNumber: 0 };
    return {
      id: sectionId,
      title: metadata.title,
      content: '',
      status: 'not-started',
      pageNumber: metadata.pageNumber
    };
  }

  // Create empty sections for all section IDs
  static createEmptySections(): Record<SectionId, BusinessPlanSection> {
    const sections: Record<SectionId, BusinessPlanSection> = {} as Record<SectionId, BusinessPlanSection>;
    
    Object.entries(SECTION_METADATA).forEach(([id, metadata]) => {
      sections[id as SectionId] = {
        id: id as SectionId,
        title: metadata.title,
        content: '',
        status: 'not-started',
        pageNumber: metadata.pageNumber,
        subsections: []
      };
    });
    
    return sections;
  }

  // Identify which section a line belongs to
  static identifySection(line: string): SectionId | null {
    if (!line) return null;
    
    // Check if the line contains a section marker
    const normalizedLine = line.toLowerCase().trim();
    
    for (const [sectionId, markers] of Object.entries(SECTION_MARKERS)) {
      for (const marker of markers) {
        if (normalizedLine.includes(marker.toLowerCase())) {
          return sectionId as SectionId;
        }
      }
    }
    
    // Check for headings that match section titles
    for (const [sectionId, metadata] of Object.entries(SECTION_METADATA)) {
      const title = metadata.title.toLowerCase();
      if (normalizedLine.includes(title)) {
        return sectionId as SectionId;
      }
    }
    
    return null;
  }
}
